import { Button, Icon, Link } from "@material-ui/core";
import CalendarRange from "mdi-material-ui/CalendarRange";
import Numeric from "mdi-material-ui/Numeric";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Star from "mdi-material-ui/Star";
import Update from "mdi-material-ui/Update";
import React, { Component } from "reactn";
import styled from "styled-components";

import { SearchThrottleWait } from "common/constants";
import { ActionPermissionsObject, WorkspaceResourceType } from "common/interfaces";
import { Breadcrumb } from "js/Components/Breadcrumb";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialogAsync, ShowErrorDialog, ShowInputDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideGeneralNotice, SharedSlideNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";
import { _ } from "js/vendor";

import { teamSlidesDataService } from "../DataServices";
import { FilterDropDown } from "./Components/FilterDropDown";
import {
    InnerWhiteFrameContainer,
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer
} from "./Components/SearchBox";
import { SortMenu } from "./Components/SortMenu";
import { TeamSlideThumbnail } from "./Components/TeamSlideThumbnail";
import { MultiSelectThumbnailGrid } from "./Components/ThumbnailGrid";

const logger = getLogger(LogGroup.TEAMS);

const filterOptions = [
    {
        id: "all_content",
        name: "All Content"
    },
    {
        id: "name",
        name: "Title"
    },
    {
        id: "description",
        name: "Description"
    },
    {
        id: "text",
        name: "Slide Content"
    },
    {
        id: "tags",
        name: "Tags"
    },
    {
        id: "template",
        name: "Template"
    },
];

const AddSlideContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    background: ${themeColors.lightGray};
    padding-top: 20px;
    //this is to cover any thumbnail spinners behind the container
    z-index: 1000;
`;

const AddSlideButton = styled(Button)`
    background: #2EB4E5 !important;
    color: white !important;
    min-width: 150px !important;
    margin-bottom: 15px !important;
    padding: 10px 20px !important;

    &:disabled {
        background: white !important;
        color: #A9A9A9 !important;
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;   
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

export const SharedSlidesPane = WorkspaceController.withInitializedState(
    class SharedSlidesPane extends Component {
        constructor(props) {
            super(props);

            this.state = {
                isLoaded: false,
                query: this.global.searchSlidesQuery ?? "",
                sort: null,
                tags: [{ id: "all_tags", name: "All Tags" }],
                selectedContent: "all_content",
                selectedTag: "all_tags",
                searchResults: [],
                selectedItems: []
            };

            this.scrollRef = React.createRef();
            this.fetchSearchResultsThrottled = _.throttle(this.fetchSearchResults, SearchThrottleWait, { leading: false });
            this._fetchSearchResultsPromise = Promise.resolve();
        }

        get sortOptions() {
            return [{
                value: "name", label: "Name", icon: <SortAlphabeticalVariant />
            }, {
                value: "ratedByCount", label: "Rating", reverse: true, icon: <Star />
            }, {
                value: "presentationCount", label: "Popularity", reverse: true, icon: <Numeric />
            }, {
                value: "createdAt", label: "Created At", reverse: true, icon: <CalendarRange />
            }, {
                value: "modifiedAt", label: "Modified At", reverse: true, icon: <Update />
            }];
        }

        componentDidMount() {
            this.fetchSearchResults();
        }

        componentDidUpdate(prevProps, prevState) {
            const { isTemplate, resources } = this.props;
            const { query, selectedContent, selectedTag } = this.state;

            if (
                prevState.query !== query ||
                prevState.selectedContent !== selectedContent ||
                prevState.selectedTag !== selectedTag ||
                prevProps.isTemplate !== isTemplate
            ) {
                this.setState({ isLoaded: false, selectedItems: [] });
                this.fetchSearchResultsThrottled();
            } else if (!_.isEqual(prevProps.resources, resources)) {
                // Reload results without showing the spinner
                this.fetchSearchResultsThrottled();
            }
        }

        setStateAsync(stateUpdate) {
            return new Promise(resolve => this.setState(stateUpdate, resolve));
        }

        fetchSearchResults = () => {
            this._fetchSearchResultsPromise = this._fetchSearchResultsPromise.then(async () => {
                const { isManageLibrary, isTemplate } = this.props;
                const { selectedContent, query, selectedTag } = this.state;

                if (!WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use) {
                    await this.setStateAsync({
                        searchResults: [],
                        tags: [],
                        isLoaded: true
                    });
                    return;
                }

                const allTagsMap = {};

                // Filter resources by type and build tag counts
                WorkspaceController.resources
                    .filter(resource => {
                        const isCorrectType = isTemplate
                            ? resource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE
                            : resource.resourceType === WorkspaceResourceType.SLIDE;

                        if (!isCorrectType) return false;

                        if (!isManageLibrary) {
                            return !resource.metadata?.isDisabled;
                        }

                        return true;
                    })
                    .forEach(resource => {
                        resource.tags.forEach(tag => {
                            if (!allTagsMap[tag]) {
                                allTagsMap[tag] = 1;
                            } else {
                                allTagsMap[tag]++;
                            }
                        });
                    });

                // Fetch filtered search results
                let searchResults = await teamSlidesDataService.search({
                    fullTextSearchQuery: query,
                    tag: selectedTag !== "all_tags" ? selectedTag : undefined,
                    fullTextSearchByFields: (selectedContent && selectedContent !== "all_content") ? [selectedContent] : [],
                    resourceTypes: isTemplate ? [WorkspaceResourceType.SLIDE_TEMPLATE] : [WorkspaceResourceType.SLIDE]
                });

                if (!isManageLibrary) {
                    searchResults = searchResults.filter(({ isDisabled }) => !isDisabled);
                }

                searchResults = searchResults.filter(slide => !!slide.isTemplate === isTemplate);

                // Build tags from all available tags with their total counts
                const tags = [
                    { id: "all_tags", name: "All Tags" },
                    ...Object.entries(allTagsMap).map(([tag, totalCount]) => {
                        return {
                            id: tag,
                            name: `${tag} (${totalCount})`
                        };
                    })
                ];

                // Add selected tag if it's not in the list
                if (selectedTag !== "all_tags" && !tags.some(({ id }) => id === selectedTag)) {
                    tags.push({ id: selectedTag, name: `${selectedTag} (0)` });
                }

                await this.setStateAsync({
                    searchResults,
                    tags,
                    isLoaded: true
                });
            });

            return this._fetchSearchResultsPromise;
        }

        handleSelectTag = event => {
            this.setState({ selectedTag: event.target.value });
        }

        handleSearch = query => {
            this.setState({ query });
        }

        handleSort = sort => {
            this.setState({ sort });
        }

        handleFilterChange = event => {
            this.setState({ selectedContent: event.target.value });
        }

        handleAddItem = async () => {
            const { resourceType } = this.props;
            AppController.showSlideEditor({ resourceType });
        }

        handleEditSlide = async item => {
            const { resourceType } = this.props;
            AppController.showSlideEditor({ slideId: item.id, resourceType });
        }

        copySlide = async (itemModel, updateProps = {}) => {
            try {
                await withProgressDialog({
                    title: "Copying slide...",
                    action: async () => {
                        const {
                            name,
                            id,
                            tags,
                            description,
                            isDisabled,
                            isTemplate,
                        } = { ...itemModel, ...updateProps };

                        const slide = new Slide({ id });
                        await slide.load();

                        const slideModel = _.cloneDeep(slide.attributes);
                        delete slideModel.id;
                        delete slideModel.libraryItemId;
                        delete slideModel.thumbnails;
                        delete slideModel.presentationId;

                        const newSlide = new Slide(slideModel, {
                            userId: app.userId,
                            autoSync: false
                        });
                        await newSlide.load();

                        const resource = {
                            resourceId: newSlide.id,
                            resourceType: isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                            // Hard to track what we can be served with... so let's make sure we're always passing an array
                            tags: tags
                                ? Array.isArray(tags)
                                    ? tags
                                    : Object.keys(tags)
                                : [],
                            folderIds: [],
                            metadata: {
                                name,
                                description,
                                isDisabled,
                                contentModifiedAt: newSlide.get("modifiedAt"),
                                usedByUsers: [],
                                usedInPresentations: [],
                                usedInPublicPresentations: [],
                                ratedByUsers: [],
                                createdBy: app.user.id,
                                modifiedBy: app.user.id,
                            }
                        };

                        await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });
                    }
                });
            } catch (err) {
                logger.error(err, "[SharedSlidesPane] copySlide failed");

                ShowErrorDialog({
                    error: "An error occurred while copying the slide.",
                    message: err.message
                });
            }
        }

        handleCopySlide = async item => {
            if (item.isTemplate) {
                await this.copySlide(item, {
                    isTemplate: false,
                    name: `Copy of ${item.name}`
                });

                ShowWarningDialog({
                    title: "A copy of this slide is now available to your team as a finished, uneditable Shared Slide.",
                    icon: "/images/ui/icons/done-check.gif"
                });

                return;
            }

            await this.copySlide(item, {
                isTemplate: true,
                name: `Copy of ${item.name}`,
                isDisabled: false
            });

            ShowWarningDialog({
                title: "A copy of this slide is now available to your team as an editable Slide Template.",
                icon: "/images/ui/icons/done-check.gif"
            });
        }

        handleDuplicateSlide = async item => {
            const name = await ShowInputDialog({
                title: "Enter a name for the copied slide",
                value: `Copy of ${item.name}`
            });

            if (!name) {
                return;
            }

            await this.copySlide(item, {
                name
            });
        }

        handleDeleteSlide = async item => {
            const { isTemplate, trackActivity } = this.props;
            const { resource: { resourceId, resourceType } } = item;

            const deleteResource = async () => {
                try {
                    await withProgressDialog({
                        title: `Deleting ${resourceType === WorkspaceResourceType.SLIDE_TEMPLATE ? "Slide Template" : "Shared Slide"}...`,
                        action: () => WorkspaceController.deleteWorkspaceResource({ resourceId, resourceType }, { trackActivity })
                    });
                } catch (err) {
                    logger.error(err, "[SharedSlidesPane]  failed", { resourceId, resourceType });

                    ShowErrorDialog({
                        error: "An error occurred while deleting the Team Slide.",
                        message: (
                            <>
                                <p>
                                    <strong>Error:</strong> {err.message}
                                </p>
                                <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                            </>
                        )
                    });
                }
            };

            if (!isTemplate) {
                if (await ShowConfirmationDialog({
                    title: "Are you sure you want to delete this Shared Slide?",
                    message: <>
                        <span>
                            Deleting a Shared Slide permanently removes it from your Team Resources.&nbsp;
                            Existing instances of this slide will no longer update automatically and will be editable by presentation editors.&nbsp;
                            <Link onClick={() => this.showTeamSlideDialog(item)}>See where this slide is being used.</Link>
                        </span>
                        <Gap20 />
                        <StatsContainer>
                            <FlexBox left middle>
                                <Icon>star</Icon>
                                {item.ratedByCount} {"like".pluralize(item.ratedByCount != 1)}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>person</Icon>
                                Used by {item.usedByCount} {item.usedByCount == 1 ? "person" : "people"}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>cloud_download</Icon>
                                Used in {item.presentationCount} {"presentation".pluralize(item.presentationCount !== 1)}
                            </FlexBox>
                        </StatsContainer>
                    </>,
                    okButtonLabel: "Delete Shared Slide",
                    buttonOptions: {
                        acceptButtonColor: "red"
                    }
                })) {
                    await deleteResource();
                }
                return;
            }

            if (await ShowConfirmationDialog({
                title: `Are you sure you want to delete this ${this.props.title}?`,
                message: `Deleting a ${this.props.title} permanently removes it from your ${this.props.title} library and prevents it from being used in any future presentations.`,
                buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
                okButtonLabel: "PERMANENTLY DELETE TEAM SLIDE"
            })) {
                await deleteResource();
            }
        }

        handleAddSelectedSlides = async () => {
            const { onSelected } = this.props;
            const { selectedItems } = this.state;

            const props = {
                ...ds.selection.presentation.getAnalytics(),
                slide_ids: selectedItems.map(item => item.id),
            };
            trackActivity("Presentation", "LibrarySlideAdded", null, null, props);

            onSelected(selectedItems);
        }

        showTeamSlideDialog = async item => {
            await ShowDialogAsync(TeamSlideDialog, {
                slideId: item.id
            });
        }

        render() {
            const {
                isManageLibrary,
                title,
                resourceType
            } = this.props;
            const {
                query,
                selectedTag,
                selectedContent,
                selectedItems,
                tags
            } = this.state;

            if (!WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use) {
                return (<UIPane>
                    {resourceType === TeamResourceTypes.SLIDE_TEMPLATES && <SharedSlideNotice />}
                    {resourceType !== TeamResourceTypes.SLIDE_TEMPLATES && <NoTeamSlideNotice />}
                </UIPane>
                );
            }

            let panelStyles, searchStyles, scrollStyles;
            if (!isManageLibrary) {
                panelStyles = {
                    paddingTop: 4,
                    paddingRight: 20,
                    paddingBottom: 0,
                    paddingLeft: 20
                };
                searchStyles = {
                    position: "initial"
                };
            } else {
                scrollStyles = {
                    height: "calc(100% - 49px)"
                };
            }

            return (
                <UIPane>
                    <SearchBarContainer className="search-container">
                        {isManageLibrary &&
                            <>
                                <Breadcrumb>{title}s</Breadcrumb>
                                <RoundIconButton onClick={this.handleAddItem}><Icon>add</Icon></RoundIconButton>
                                <Gap size={60} />
                            </>
                        }
                        <SearchBarInnerContainer>
                            <SlideSearchInput
                                query={query}
                                onSearch={this.handleSearch}
                                placeholder="Search team slides…"
                            />
                            <FilterDropDown
                                onChange={this.handleFilterChange}
                                selectedFilter={selectedContent}
                                filterOptions={filterOptions}
                            />
                            <FilterDropDown
                                onChange={this.handleSelectTag}
                                selectedFilter={selectedTag}
                                filterOptions={tags}
                            />
                            <SortMenu
                                disabled={query.length > 0}
                                sortOptions={this.sortOptions}
                                onSetSort={this.handleSort}
                            />
                        </SearchBarInnerContainer>
                    </SearchBarContainer>
                    <UIPaneResultsContainer>
                        {this.renderSlides()}
                    </UIPaneResultsContainer>
                    {!isManageLibrary &&
                        <AddSlideContainer>
                            <AddSlideButton disabled={!selectedItems.length} onClick={this.handleAddSelectedSlides}>
                                Add Selected Slides
                            </AddSlideButton>
                        </AddSlideContainer>
                    }
                </UIPane>
            );
        }

        renderSlides() {
            const {
                isManageLibrary,
                resourceType
            } = this.props;
            const {
                isLoaded,
                query,
                searchResults,
                sort
            } = this.state;

            if (!isLoaded) {
                return <Spinner />;
            }

            if (!searchResults.length) {
                if (!query) {
                    if (resourceType === TeamResourceTypes.SLIDE_TEMPLATES) {
                        if (isManageLibrary) return <SharedSlideNotice />;
                        else {
                            // We added this because we we are not in the manageing library
                            // We should present to any user this default message regardless of
                            // their role
                            return (<SharedSlideGeneralNotice />);
                        }
                    }
                    return <NoTeamSlideNotice />;
                } else {
                    return <NoMatchNotice />;
                }
            }

            const sortedResults = sort
                ? _.sortBy(searchResults, item => {
                    if (typeof item[sort.field] === "string") {
                        return item[sort.field].toLowerCase().trim();
                    } else {
                        return item[sort.field];
                    }
                })
                : searchResults;
            if (sort?.reverse) {
                sortedResults.reverse();
            }

            return (<InnerWhiteFrameContainer>
                <MultiSelectThumbnailGrid
                    columns={3}
                    items={sortedResults}
                    allowMultiSelect={!isManageLibrary}
                    thumbnailClass={TeamSlideThumbnail}
                    thumbnailProps={{
                        isManageLibrary,
                        onEditSlide: this.handleEditSlide,
                        onDeleteSlide: this.handleDeleteSlide,
                        onCopySlide: this.handleCopySlide,
                        onDuplicateSlide: this.handleDuplicateSlide
                    }}
                    onItemSelected={selectedItems => this.setState({ selectedItems })}
                    onDoubleClick={slide => {
                        if (isManageLibrary) {
                            this.showTeamSlideDialog(slide);
                        } else {
                            this.setState({ selectedItems: [slide] }, () => {
                                this.handleAddSelectedSlides();
                            });
                        }
                    }}
                />
            </InnerWhiteFrameContainer>);
        }
    }
);
